/**
 * A registrar-supplied roster file, read into the typed import's rows (ADR 0036).
 *
 * The registrar brings a CSV or a JSON file; the import takes keyed rows. This
 * module is the mapping between them and nothing more: it derives each row's
 * `import_key` from the facts the registrar wrote, so a re-exported roster
 * resolves to the same rows, and it names what it could not read instead of
 * guessing. The server validates every row again, per row, on the dry run.
 *
 * CSV columns (header row, any order, extra columns refused):
 *
 *   kind               student | enrollment
 *   student_id         an existing IMAM SID, or blank to issue a new one
 *   given_name, family_name
 *   delivery_email     the contact the invitation will use; never an identity
 *   program_key        the program's code, optional
 *   cohort_key         the cohort's name, optional
 *   admitted_year      four digits, optional (cohort year for a new SID)
 *   student_key        enrollment rows: the student row's key (its SID, or
 *                      the given name, family name and contact joined by |)
 *   course_code, term_name
 *
 * The key of a student row is its SID when it has one; otherwise the three
 * facts that name a new person, joined by `|`, folded to lower case. The key
 * of an enrollment row is its student key, course code and term name joined
 * the same way. Keys are the registrar's facts, so the registrar can read
 * them on the receipts and a changed name is a changed row.
 */
import type { ImportRowInput } from "@/lib/api/imports";

export const ROSTER_COLUMNS = [
  "kind",
  "student_id",
  "given_name",
  "family_name",
  "delivery_email",
  "program_key",
  "cohort_key",
  "admitted_year",
  "student_key",
  "course_code",
  "term_name",
] as const;

type RosterColumn = (typeof ROSTER_COLUMNS)[number];

export interface RosterReading {
  rows: ImportRowInput[];
  /** Lines the reader could not turn into a row, each with its line number. */
  problems: string[];
}

function fold(value: string): string {
  return value.trim().replace(/\s+/g, " ").toLowerCase();
}

export function studentKey(fields: { student_id?: string; given_name?: string; family_name?: string; delivery_email?: string }): string {
  const sid = fields.student_id?.trim();
  if (sid) return sid;
  return [fields.given_name ?? "", fields.family_name ?? "", fields.delivery_email ?? ""].map(fold).join("|");
}

export function enrollmentKey(fields: { student_key: string; course_code: string; term_name: string }): string {
  return [fields.student_key.trim(), fold(fields.course_code), fold(fields.term_name)].join("|");
}

/** RFC 4180 enough for a registrar export: quoted fields, doubled quotes, CRLF. */
export function parseCsv(text: string): string[][] {
  const records: string[][] = [];
  let record: string[] = [];
  let field = "";
  let quoted = false;
  for (let index = 0; index < text.length; index += 1) {
    const char = text[index];
    if (quoted) {
      if (char === '"') {
        if (text[index + 1] === '"') {
          field += '"';
          index += 1;
        } else {
          quoted = false;
        }
      } else {
        field += char;
      }
      continue;
    }
    if (char === '"') {
      quoted = true;
    } else if (char === ",") {
      record.push(field);
      field = "";
    } else if (char === "\n" || char === "\r") {
      if (char === "\r" && text[index + 1] === "\n") index += 1;
      record.push(field);
      records.push(record);
      record = [];
      field = "";
    } else {
      field += char;
    }
  }
  if (field !== "" || record.length > 0) {
    record.push(field);
    records.push(record);
  }
  return records.filter((row) => row.some((cell) => cell.trim() !== ""));
}

function rowFromFields(fields: Partial<Record<RosterColumn, string>>, line: number): ImportRowInput | string {
  const kind = fold(fields.kind ?? "");
  const present = (name: RosterColumn) => (fields[name] ?? "").trim();
  if (kind === "student") {
    const data: Record<string, unknown> = {
      given_name: present("given_name"),
      family_name: present("family_name"),
    };
    if (present("student_id")) data.student_id = present("student_id");
    if (present("delivery_email")) data.delivery_email = present("delivery_email");
    if (present("program_key")) data.program_key = present("program_key");
    if (present("cohort_key")) data.cohort_key = present("cohort_key");
    if (present("admitted_year")) {
      const year = Number.parseInt(present("admitted_year"), 10);
      if (!Number.isInteger(year)) return `line ${line}: admitted_year ${present("admitted_year")} is not a year`;
      data.admitted_year = year;
    }
    for (const stray of ["student_key", "course_code", "term_name"] as const) {
      if (present(stray)) return `line ${line}: a student row does not carry ${stray}`;
    }
    return {
      entity_kind: "student",
      import_key: studentKey(data as { student_id?: string; given_name: string; family_name: string; delivery_email?: string }),
      source_ref: { line },
      data,
    };
  }
  if (kind === "enrollment") {
    const key = present("student_key");
    const course = present("course_code");
    const term = present("term_name");
    if (!key || !course || !term) {
      return `line ${line}: an enrollment row needs student_key, course_code and term_name`;
    }
    for (const stray of ["student_id", "given_name", "family_name", "delivery_email", "program_key", "cohort_key", "admitted_year"] as const) {
      if (present(stray)) return `line ${line}: an enrollment row does not carry ${stray}`;
    }
    return {
      entity_kind: "enrollment",
      import_key: enrollmentKey({ student_key: key, course_code: course, term_name: term }),
      source_ref: { line },
      data: { student_key: key, course_code: course, term_name: term },
    };
  }
  return `line ${line}: kind must be student or enrollment, not ${JSON.stringify(fields.kind ?? "")}`;
}

export function readRosterCsv(text: string): RosterReading {
  const records = parseCsv(text);
  if (records.length === 0) return { rows: [], problems: ["the file is empty"] };
  const header = records[0].map((cell) => fold(cell));
  const unknown = header.filter((name) => !(ROSTER_COLUMNS as readonly string[]).includes(name));
  if (unknown.length > 0) {
    return { rows: [], problems: [`unknown column${unknown.length === 1 ? "" : "s"}: ${unknown.join(", ")}`] };
  }
  if (!header.includes("kind")) return { rows: [], problems: ["the header has no kind column"] };
  const rows: ImportRowInput[] = [];
  const problems: string[] = [];
  records.slice(1).forEach((record, offset) => {
    const line = offset + 2;
    const fields: Partial<Record<RosterColumn, string>> = {};
    header.forEach((name, column) => {
      fields[name as RosterColumn] = record[column] ?? "";
    });
    const row = rowFromFields(fields, line);
    if (typeof row === "string") problems.push(row);
    else rows.push(row);
  });
  return { rows, problems };
}

function isRowEnvelope(value: unknown): value is { rows: unknown[] } {
  return typeof value === "object" && value !== null && Array.isArray((value as { rows?: unknown }).rows);
}

export function readRosterJson(text: string): RosterReading {
  let parsed: unknown;
  try {
    parsed = JSON.parse(text);
  } catch {
    return { rows: [], problems: ["the file is not valid JSON"] };
  }
  const list = Array.isArray(parsed) ? parsed : isRowEnvelope(parsed) ? parsed.rows : null;
  if (!list) return { rows: [], problems: ["the file is neither a list of rows nor an object with a rows list"] };
  const rows: ImportRowInput[] = [];
  const problems: string[] = [];
  list.forEach((item, index) => {
    if (typeof item !== "object" || item === null) {
      problems.push(`row ${index + 1} is not an object`);
      return;
    }
    const candidate = item as Partial<ImportRowInput>;
    if (candidate.entity_kind !== "student" && candidate.entity_kind !== "enrollment") {
      problems.push(`row ${index + 1}: entity_kind must be student or enrollment`);
      return;
    }
    if (typeof candidate.import_key !== "string" || !candidate.import_key.trim()) {
      problems.push(`row ${index + 1}: import_key is required`);
      return;
    }
    rows.push({
      entity_kind: candidate.entity_kind,
      import_key: candidate.import_key,
      source_ref: candidate.source_ref ?? { row: index + 1 },
      data: candidate.data ?? {},
    });
  });
  return { rows, problems };
}

/** Read a roster file by its name's extension: `.csv` by the column contract, `.json` as typed rows. */
export function readRosterFile(name: string, text: string): RosterReading {
  const lower = name.toLowerCase();
  if (lower.endsWith(".csv")) return readRosterCsv(text);
  if (lower.endsWith(".json")) return readRosterJson(text);
  return { rows: [], problems: [`${name} is neither a .csv nor a .json file`] };
}

/** A roster file's rows, summarised the way the registrar counts them. */
export function summariseRoster(rows: ImportRowInput[]): { students: number; newStudents: number; enrollments: number } {
  const students = rows.filter((row) => row.entity_kind === "student");
  return {
    students: students.length,
    newStudents: students.filter((row) => !(row.data as { student_id?: unknown } | undefined)?.student_id).length,
    enrollments: rows.filter((row) => row.entity_kind === "enrollment").length,
  };
}
